import {Injectable, inject, Injector} from '@angular/core';
import {Platform} from '@ionic/angular';
import {Router} from '@angular/router';
import {StorageService} from 'src/app/services/storage.service';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {BehaviorSubject, forkJoin, from, Observable, of, throwError} from 'rxjs';
import {map, switchMap, tap, catchError} from 'rxjs/operators';
import {environment} from '../../environments/environment';
import {LoggerService} from 'src/app/services/logger.service';
import {JobActionsService} from 'src/app/services/job-actions.service';
import {AdAttributionService} from 'src/app/services/ad-attribution.service';
import {NavController, AlertController} from '@ionic/angular';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private injector = inject(Injector);
  authenticationState = new BehaviorSubject(false);
  invoiceId = new BehaviorSubject(null);
  orderState = new BehaviorSubject({});
  userInfo = new BehaviorSubject({});
  cardData = new BehaviorSubject({});
  invoiceInfo = new BehaviorSubject([]);

  mechanicData = new BehaviorSubject({});
  authUser: any;
  reducedUser: any;
  isLoggedIn = false;
  token: any;
  serviceFee: any;
  companyName: any;
  diagnosFee: any;
  notificationsUnreadCount = 0;
  isTabSelected = 0;
  signUpId = new BehaviorSubject(0);
  /** Set by SocialAuthService when /auth/social answers SOCIAL_NEW_USER; read by the signup page. */
  socialHandoff: import('./social-auth.service').SocialHandoff | null = null;
  email: any;
  id: any;
  res_data: any;
  currentTimeZone: any;

  constructor(
    private platform: Platform,
    private router: Router,
    public storage: StorageService,
    private http: HttpClient,
    private navCtrl: NavController
  ) {
    this.platform.ready().then(async () => {
      this.currentTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    });
  }

  isAuthenticated() {
    return this.authenticationState.value;
  }

  // Lazy resolve (via Injector, like LoggerService above) rather than a direct
  // inject() field: AdAttributionService itself injects LoggerService, and
  // LoggerService injects AuthService eagerly, so a direct field here would
  // create a circular DI chain (AuthService -> AdAttributionService ->
  // LoggerService -> AuthService) at construction time.
  private flushAdAttribution(): void {
    this.injector
      .get(AdAttributionService)
      .flush()
      .catch(() => {});
  }

  // Function to convert a local time to UTC
  // Templates bind this directly (order cards, chat, order detail), so Angular
  // re-runs it on every change-detection pass: two calls per order card,
  // 100+ cards, dozens of passes while a list settles. toLocaleString with a
  // timeZone builds a fresh Intl.DateTimeFormat each call — milliseconds on
  // a phone — and that alone held the Completed tab for seconds after the
  // response had arrived (profiled 2026-09-26: 42 of 50 sampled seconds).
  // One formatter per zone, results memoized by input string.
  private localTimeFormatter?: Intl.DateTimeFormat;
  private localTimeFormatterZone?: string;
  private localTimeCache = new Map<string, string>();

  convertToLocalUTC(localTime: string): string {
    if (!localTime) return '';
    const cached = this.localTimeCache.get(localTime);
    if (cached !== undefined) return cached;
    if (!this.localTimeFormatter || this.localTimeFormatterZone !== this.currentTimeZone) {
      // Same output as Date#toLocaleString('en-US', { timeZone }): numeric
      // date and time, e.g. "9/26/2026, 7:14:22 PM".
      this.localTimeFormatter = new Intl.DateTimeFormat('en-US', {
        timeZone: this.currentTimeZone,
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        second: 'numeric',
      });
      this.localTimeFormatterZone = this.currentTimeZone;
    }
    const formatted = this.localTimeFormatter.format(new Date(localTime.replace(' ', 'T') + 'Z'));
    if (this.localTimeCache.size >= 5000) this.localTimeCache.clear();
    this.localTimeCache.set(localTime, formatted);
    return formatted;
  }
  async getUserAsync() {
    // Node-only session: restore needs the user row + stored creds (the
    // interceptor re-mints an expired 8h newtoken from reduced_user). The
    // legacy 'token' key is gone — old installs may still carry one; ignored.
    Promise.all([
      this.storage.get('user'),
      this.storage.get('newtoken'),
      this.storage.get('reduced_user'),
      this.storage.get('refresh_token'),
    ]).then((res) => {
      const [user, newToken, reducedUser, refreshToken] = res;
      // Social sessions have no reduced_user; their long-lived credential is
      // the refresh token.
      if (user && newToken && (reducedUser || refreshToken)) {
        this.isLoggedIn = true;
        this.authUser = user;
        this.authenticationState.next(true);
        this.userInfo.next(user);
      }
    });
  }

  getTokenFromStorage(): Observable<any> {
    const token = from(this.storage.get('newtoken'));

    return token.pipe(
      switchMap((token) => {
        return of(token);
      })
    );
  }

  getTokenFromApi(): Observable<string> {
    const creds = from(
      Promise.all([this.storage.get('refresh_token'), this.storage.get('reduced_user')])
    );
    return creds.pipe(
      switchMap(([refreshToken, reducedUser]) => {
        // Preferred: rotate the server-issued refresh token (social sessions,
        // and any password session that opted in). No credentials leave the
        // device. A 401 here means the token was revoked/reused/expired — the
        // interceptor's self-heal then forces a clean re-login.
        if (refreshToken) {
          return this.http
            .post(environment.API_URL + 'auth/refresh-token', {refresh_token: refreshToken})
            .pipe(
              map((res: any) => {
                if (!res?.token || !res?.refresh_token) {
                  throw new Error('Refresh-token response missing token fields');
                }
                this.storage.set('newtoken', res.token);
                this.storage.set('refresh_token', res.refresh_token);
                return res.token as string;
              }),
              catchError((err) => {
                return throwError(() => err);
              })
            );
        }
        if (reducedUser) {
          const loginBody = {
            email: reducedUser.email,
            user_group: reducedUser.user_group,
            password: reducedUser.password,
          };
          return this.http.post(environment.API_URL + 'auth/login', loginBody).pipe(
            map((res: any) => {
              // FIX C: a 200 with no token field must error — emitting undefined
              // would cause 'Bearer undefined' retries and hang waiters in the filter.
              if (!res.token) {
                throw new Error('Token refresh response missing token field');
              }
              this.storage.set('newtoken', res.token);
              return res.token;
            }),
            catchError((err) => {
              return throwError(() => err);
            })
          );
        }
        return throwError(() => new Error('No user credentials available for token refresh'));
      })
    );
  }

  /**
   * Single choke point for persisting session credentials. reduced_user is
   * replayed by the token interceptor to mint Node API tokens; a stale copy
   * (password changed server-side, sentinel value, another user's creds)
   * strands the device in a 401 loop that the long-lived legacy token hides.
   * Every write must come through here so the stored copy tracks reality.
   */
  async setStoredCredentials(creds: {email: string; password: string; user_group: any}) {
    this.reducedUser = creds;
    await this.storage.set('reduced_user', creds);
  }

  /**
   * Sync the stored credential copy after a server-side password change.
   * With an email argument, only updates when it matches the stored session
   * (guards flows that can run for a different account); without one, updates
   * the current session's creds.
   */
  async updateStoredPassword(newPassword: string, email?: string) {
    const stored = await this.storage.get('reduced_user');
    if (!stored) {
      return;
    }
    if (email && stored.email?.toLowerCase() !== email.toLowerCase()) {
      return;
    }
    await this.setStoredCredentials({...stored, password: newPassword});
  }

  // Server rejections (401/403) carry the v13 {status:false, message} envelope
  // in the error body — surface them into the callers' status branch, which is
  // exactly how the PHP HTTP-200 failures flowed. Anything without that
  // envelope (network, 5xx) stays an error.
  private normalizeLoginRejection(err: any): Observable<any> {
    if (err?.error && typeof err.error === 'object' && typeof err.error.message === 'string') {
      if (err.status === 401) {
        this.injector.get(LoggerService).logInformation('Login rejected (401)');
      }
      return of(err.error);
    }
    this.injector
      .get(LoggerService)
      .logError(`Login failed (status ${err?.status ?? 'unknown'})`, {stack: err?.stack});
    return throwError(() => err);
  }

  doLogin(data) {
    return this.http.post(environment.API_URL + 'auth/login', data).pipe(
      catchError((err) => this.normalizeLoginRejection(err)),
      tap((res: any) => {
        this.res_data = res.message;
        this.storage.set('msg_login_err', this.res_data);
        if (res && res.status === true) {
          this.storage.set('user_info', res.data);
          this.reducedUser = {
            email: data.email,
            password: data.password,
            user_group: res.data.user_group,
          };
          if (res.is_verified == 1) {
            this.updateUserData(res);
          } else {
            // Unverified: token is minted but the user goes to confirm-email;
            // the server re-mailed their stored OTP with this response.
            this.id = res.data.id;
            this.signUpId.next(res.data.id);
            this.userInfo.next(res.data);
            this.navCtrl.navigateForward(['/signup/confirm-email']);
          }
        }
      })
    );
  }

  dobuildLogin(data, id: any) {
    return this.http.post(environment.API_URL + 'auth/login', data).pipe(
      catchError((err) => this.normalizeLoginRejection(err)),
      tap((res: any) => {
        this.res_data = res.message;
        this.storage.set('msg_login_err', this.res_data);
        if (res && res.status === true) {
          this.storage.set('user_info', res.data);
          this.reducedUser = {
            email: data.email,
            password: data.password,
            user_group: res.data.user_group,
          };
          this.setStroage(res, id);
        }
      })
    );
  }

  // ONE token: the Node 8h JWT, stored as 'newtoken'. The interceptor re-mints
  // it from reduced_user when it expires; nothing writes the legacy 'token'
  // key any more.
  async setStroage(data, id) {
    this.isLoggedIn = true;
    this.authUser = data.data;
    // Only save reduced_user if it's actually set (don't overwrite with undefined)
    if (this.reducedUser) {
      await this.setStoredCredentials(this.reducedUser);
    }
    await this.storage.set('newtoken', data.token);
    await this.storage.set('user', this.authUser);
    await this.storage.set('USER_GROUP', data.data.user_group);
    this.userInfo.next(this.authUser);
    this.authenticationState.next(true);
    this.invoiceId.next(id);
    this.flushAdAttribution();
  }

  async updateUserData(data: any) {
    this.isLoggedIn = true;
    this.authUser = data.data;
    // Only save reduced_user if it's actually set (don't overwrite with undefined)
    if (this.reducedUser) {
      await this.setStoredCredentials(this.reducedUser);
    }
    await this.storage.set('newtoken', data.token);
    await this.storage.set('user', this.authUser);
    await this.storage.set('USER_GROUP', data.data.user_group);
    this.authenticationState.next(true);
    this.userInfo.next(this.authUser);
    this.flushAdAttribution();
  }

  /**
   * Store a successful /auth/social envelope. Same session shape as doLogin,
   * except the long-lived credential is the refresh token — there is no
   * password to replay, so reduced_user is cleared rather than poisoned.
   */
  async completeSocialLogin(res: any) {
    this.reducedUser = undefined;
    this.socialHandoff = null;
    await this.storage.remove('reduced_user');
    await this.storage.remove('social_handoff');
    if (res.refresh_token) {
      await this.storage.set('refresh_token', res.refresh_token);
    }
    await this.storage.set('user_info', res.data);
    if (res.is_verified == 1) {
      await this.updateUserData(res);
    } else {
      this.id = res.data.id;
      this.signUpId.next(res.data.id);
      this.userInfo.next(res.data);
      this.navCtrl.navigateForward(['/signup/confirm-email']);
    }
  }

  register(data) {
    return this.http.post(environment.API_URL + 'auth/register', data);
  }

  verifyOtp(data) {
    return this.http.post(environment.API_URL + 'auth/verify-otp', data);
  }

  // tslint:disable-next-line:variable-name
  resendOtp(user_id) {
    return this.http.post(environment.API_URL + 'auth/resend-verification', {
      user_id,
    });
  }

  saveBilling(data) {
    // Node port (wave 6). PUBLIC route: the signup billing step posts user_id
    // before any token exists; the interceptor adds newtoken when logged in.
    return this.http.post(environment.API_URL + 'auth/save-billing', data);
  }

  submitSurvey(data) {
    return this.http.post(environment.API_URL + 'auth/survey', data);
  }

  singUpRegister(data) {
    return this.http.post(environment.API_URL + 'auth/next-register', data);
  }

  changePassword(data) {
    return this.http.post(environment.API_URL + 'mechanic/change-password', data).pipe(
      tap((res: any) => {
        // Keep the interceptor's stored creds in step with the new password —
        // otherwise the next Node token refresh replays the old one and the
        // self-heal logs the user out hours after a successful change.
        if (res && res.status === true && data?.new_password) {
          this.updateStoredPassword(data.new_password);
        }
      })
    );
  }

  getMechanicBussinessInfo() {
    return this.http.get(environment.API_URL + 'auth/get-mechanic-business-info');
  }

  contactUs(data) {
    return this.http.post(environment.API_URL + 'user/contacts', data);
  }

  notificationSetting(data) {
    return this.http.post(environment.API_URL + 'auth/notification-setting', data);
  }

  getNotificationSetting(data) {
    return this.http.post(environment.API_URL + 'auth/get-notification-setting', data);
  }

  /**
   * The stored totals for a job's invoice — what the server calculated, not a
   * rate applied on the client. Screens showing a price for an existing job
   * should read this rather than multiplying a subtotal by 8.25%, which is
   * wrong wherever labor and parts are taxed differently.
   */
  invoiceTotals(job_id: any) {
    return this.http.post(environment.API_URL + 'user/invoice-totals', { job_id });
  }

  myCards() {
    // Node port (wave 6) — same legacy envelope incl. the
    // {status:false,"Cards not found."} 200 on an empty list.
    return this.http.get(environment.API_URL + 'auth/my-cards');
  }

  mechanicNotifications() {
    return this.http.get(environment.API_URL + 'mechanic/mechanic-notification');
  }

  consumerNotifications() {
    return this.http.get(environment.API_URL + 'user/user-notification').pipe(
      tap((res: any) => {
        if (res) {
          this.diagnosFee = res.diagnos_fee;
          this.serviceFee = res.service_fee;
          this.companyName = res.company_name;
          if (res.status === true) {
            this.notificationsUnreadCount = res.data.filter((n) => n.read == '0').length;
          }
        }
      })
    );
  }

  markReadNotificaion() {
    return this.http.post(environment.API_URL + 'user/seen-job', {});
  }

  /** Activity → Repair updates → "Mark all read". */
  markAllNotificationsRead() {
    return this.http.post(environment.API_URL + 'user/notifications/read-all', {});
  }

  singleNotificaion(id) {
    const form = new FormData();
    form.append('notification_id', id);
    return this.http.post(environment.API_URL + 'user/seen-job-single', form);
  }

  getUserInvoice(para: any) {
    return this.http.post(environment.API_URL + 'user/user-invoice', para);
  }

  getTermsAndCondition() {
    return this.http.get(environment.API_URL + 'auth/get-terms-and-condition');
  }
  getSurveyList() {
    return this.http.get(environment.API_URL + 'auth/get-survey-list');
  }

  getNearestMechanic(data) {
    return this.http.post(environment.API_URL + 'auth/get-nearest-mechanic', data);
  }

  updateProfile(data: any) {
    return this.http.post(environment.API_URL + 'user/update-profile', data);
  }
  updateMechanicBusinessProfile(data: any) {
    return this.http.post(environment.API_URL + 'mechanic/update-profile', data);
  }

  getMechanicProfile() {
    return this.http.post(environment.API_URL + 'mechanic/get-profile', {});
  }


  // Cut over to the Node cancel cascade. The empty body makes the server record
  // the same cancel-reason row every consumer cancel has always written
  // (reason '' + 'I changed my mind').
  cancelOrderByConsumer(order_id: any) {
    return this.injector.get(JobActionsService).cancelOrder(order_id);
  }

  resetPasswordOtp(data: any) {
    return this.http.post(environment.API_URL + 'auth/send-recovery-otp', data);
  }

  forgotPassword(data: any) {
    return this.http.post(environment.API_URL + 'auth/reset-password-otp', data);
  }

  /**
   * Clears the local session and redirects to /login without making an HTTP call.
   * Used when the legacy API returns 401 (session already dead server-side).
   */
  forceLogout() {
    this.storage.remove('token');
    this.storage.remove('newtoken');
    this.storage.remove('user');
    this.storage.remove('user_info');
    this.storage.remove('reduced_user');
    this.storage.remove('refresh_token');
    this.storage.remove('social_handoff');
    this.socialHandoff = null;
    this.storage.remove('USER_GROUP');
    this.authenticationState.next(false);
    this.authUser = undefined;
    this.navCtrl.navigateRoot('/login');
  }

  // With a refresh token on the device, POST /auth/logout revokes it (and
  // clears fcm like the legacy call); otherwise the legacy GET. Either way the
  // local copy goes — callers wipe the rest of the session on 2xx as before.
  logout() {
    return from(this.storage.get('refresh_token')).pipe(
      switchMap((refreshToken) =>
        refreshToken
          ? this.http.post(environment.API_URL + 'auth/logout', {refresh_token: refreshToken})
          : this.http.get(environment.API_URL + 'user/logout', {})
      ),
      tap(() => {
        this.storage.remove('refresh_token');
        this.storage.remove('social_handoff');
        this.socialHandoff = null;
      })
    );
  }
  deleteAccount(data: any) {
    return this.http.post(environment.API_URL + 'auth/delete-account', data);
  }
  // The server maps any status other than 'active' to user.status '4'
  // (deactivated; login then refuses with the support message). The legacy
  // app always posted 'active' here, so the button never deactivated anyone —
  // owner-reported 2026-09-11, fixed to actually deactivate.
  deActivateAccount(data: any) {
    return this.http.post(environment.API_URL + 'auth/deactivate-account', {
      status: 'inactive',
    });
  }
  updateServicePartDetail(data, authToken?) {
    return this.http.post(environment.API_URL + 'user/update-service', data);
  }

  build_QuoteLogin(data) {
    return this.http.post(environment.API_URL + 'auth/build-quote-update-profile', data);
  }

  searchEmail(data) {
    return this.http.post(environment.API_URL + 'auth/get-user-data', data);
  }
}
